import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import logo from '../../../assets/images/IMT_logo.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDay, faClock, faBriefcase, faBuilding, faUser, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { getAbsoluteMediaUrl } from '../../../utils/mediaUtils';
import notificationService from '../../../services/notificationService';
import ParticleNotificationBell from './ParticleNotificationBell';
import NotificationOverviewModal from '../../NotificationOverviewModal';
import './HeroSection.css';

const HeroSection = ({ metrics, userInfo }) => {
  const history = useHistory();
  const [ref, inView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [showOverviewModal, setShowOverviewModal] = useState(false);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch notification stats for badge count
  useEffect(() => {
    const fetchNotificationStats = async () => {
      try {
        const stats = await notificationService.getStats();
        setUnreadNotificationCount(stats.unread || 0);
      } catch (error) {
        console.error('Error fetching notification stats:', error);
        setUnreadNotificationCount(0);
      }
    };

    fetchNotificationStats();

    // Refresh counts every 30 seconds for better responsiveness
    const intervalId = setInterval(fetchNotificationStats, 30000);

    // Listen for immediate updates when notifications are marked read/unread
    const handleNotificationChange = () => {
      fetchNotificationStats();
    };
    
    window.addEventListener('notificationStatsChanged', handleNotificationChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('notificationStatsChanged', handleNotificationChange);
    };
  }, []);

  // Format date and time
  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const getGreeting = () => {
    const hour = currentDateTime.getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getDayOfWeek = () => {
    return currentDateTime.toLocaleDateString('en-GB', { weekday: 'long' });
  };

  const getWeekNumber = () => {
    const date = new Date(currentDateTime);
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };

  // Format position for display (e.g., management_admin → Management/Admin)
  const formatPosition = (position) => {
    if (!position) return 'Team Member';
    
    const positionMap = {
      'ceo': 'CEO',
      'cfo': 'CFO',
      'managing_director': 'Managing Director',
      'head_of_operations': 'Head of Operations',
      'head_of_accounting_controlling': 'Head of Accounting/Controlling',
      'branch_manager': 'Branch Manager',
      'market_manager': 'Market Manager',
      'deputy_manager': 'Deputy Manager',
      'team_leader': 'Team Leader',
      'project_manager': 'Project Manager',
      'lubricants_manager': 'Lubricants Manager',
      'senior_trader': 'Senior Trader',
      'senior_operator': 'Senior Operator',
      'trader': 'Trader',
      'supply_trader': 'Supply Trader',
      'customer_trader': 'Customer Trader',
      'bunker_trader': 'Bunker Trader',
      'lubricants_trader': 'Lubricants Trader',
      'operator': 'Operator',
      'operator_assistant': 'Operator Assistant',
      'administrator': 'Administrator',
      'administrator_assistant': 'Administrator Assistant',
      'accountant': 'Accountant',
      'accountant_controlling': 'Accountant Controlling',
      'legal': 'Legal',
      'driver': 'Driver',
      'trader_purchases': 'Trader Purchases',
      'trader_sales': 'Trader Sales',
      'management_admin': 'Management/Admin'
    };
    
    return positionMap[position] || position;
  };

  // Get absolute URL for avatar photo
  const avatarUrl = userInfo?.avatar_photo ? getAbsoluteMediaUrl(userInfo.avatar_photo) : null;

  // Handle notification button click - Open modal instead of navigating
  const handleNotificationClick = () => {
    setShowOverviewModal(true);
  };

  return (
    <section className="hero-section" ref={ref}>
      {/* Background Image - Will be added via CSS */}
      <div className="hero-background" />
      
      {/* Gradient Overlay */}
      <div className="hero-overlay" />

      {/* Content */}
      <div className="hero-content">
        {/* Horizontal Stack: Logo + Profile + DateTime */}
        <div className="hero-horizontal-stack">
          {/* HMS Logo */}
          <div className="hero-logo-section">
            <img 
              src={logo} 
              alt="IMT" 
              className="hero-logo"
            />
          </div>

          {/* User Profile Section */}
          <div className="hero-profile-section">
          {/* Avatar */}
          {avatarUrl ? (
            <img 
              src={avatarUrl} 
              alt={`${userInfo.first_name} ${userInfo.last_name}`}
              className="hero-avatar"
              onError={(e) => {
                console.error('Failed to load avatar:', avatarUrl);
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className="hero-avatar-placeholder"
            style={{ display: avatarUrl ? 'none' : 'flex' }}
          >
            <FontAwesomeIcon icon={faUser} />
          </div>

          {/* User Info */}
          <div className="hero-user-details">
            <h2 className="hero-greeting">{getGreeting()}</h2>
            <h1 className="hero-user-name">
              {userInfo?.first_name} {userInfo?.last_name}
            </h1>
            <div className="hero-user-meta">
              <span className="meta-item">
                <FontAwesomeIcon icon={faBriefcase} />
                {formatPosition(userInfo?.position)}
              </span>
              <span className="meta-divider">•</span>
              <span className="meta-item">
                <FontAwesomeIcon icon={faBuilding} />
                {userInfo?.department || 'IMT'}
              </span>
              {userInfo?.office && (
                <>
                  <span className="meta-divider">•</span>
                  <span className="meta-item">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    {userInfo.office}
                  </span>
                </>
              )}
              {userInfo?.location && (
                <>
                  <span className="meta-divider">•</span>
                  <span className="meta-item">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    {userInfo.location}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Date and Time Section */}
        <div className="hero-datetime-section">
          {/* Particle Constellation Notification Button */}
          <ParticleNotificationBell 
            unreadCount={unreadNotificationCount}
            onClick={handleNotificationClick}
          />

          <div className="datetime-card-combined">
            <div className="datetime-item">
              <FontAwesomeIcon icon={faCalendarDay} className="datetime-icon" />
              <div className="datetime-content">
                <span className="datetime-label">Date</span>
                <span className="datetime-value">{formatDate(currentDateTime)}</span>
                <span className="datetime-meta">Week {getWeekNumber()} of {currentDateTime.getFullYear()}</span>
              </div>
            </div>
            
            <div className="datetime-item">
              <FontAwesomeIcon icon={faClock} className="datetime-icon" />
              <div className="datetime-content">
                <span className="datetime-label">Time</span>
                <span className="datetime-value">{formatTime(currentDateTime)}</span>
                <span className="datetime-meta">{getDayOfWeek()}</span>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Notification Overview Modal */}
      {showOverviewModal && (
        <NotificationOverviewModal
          isOpen={showOverviewModal}
          onClose={() => setShowOverviewModal(false)}
        />
      )}
    </section>
  );
};

export default HeroSection;

